import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { todosApi } from '../api/clients';
import { toApiError } from '../api/problem';
import { TargetTimeFields } from '../components/TargetTimeFields';
import { useToast } from '../components/Toasts';
import { DragItemProps, useDragReorder } from '../components/useDragReorder';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon } from '../components/icons';
import { useI18n } from '../i18n/I18nProvider';
import {
  addDays,
  formatClock,
  formatDayLong,
  formatDayShort,
  isOverdue,
  isValidDateKey,
  todayKey
} from '../utils/date';
import { PRIORITY_LABEL_KEY, PRIORITY_ORDER, PRIORITY_TAG_CLASS } from '../utils/priority';
import { PROGRESS_ACCENT_CLASS, PROGRESS_LABEL_KEY, PROGRESS_TAG_CLASS } from '../utils/taskStatus';
import {
  CreateTodoCommand,
  DaySummaryDto,
  MoveTodoRequest,
  PriorityLevel,
  ReorderTodosCommand,
  TargetTimeInput,
  TargetTimeMode,
  TodoBriefDto,
  TodoProgressKind
} from '../web-api-client';

type OverallFilter = 'all' | TodoProgressKind;

export function DayPage() {
  const params = useParams<{ date: string }>();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showToast } = useToast();

  const date = isValidDateKey(params.date) ? params.date : todayKey();

  const [todos, setTodos] = useState<TodoBriefDto[]>([]);
  const [summary, setSummary] = useState<DaySummaryDto | undefined>();
  const [carryOver, setCarryOver] = useState<TodoBriefDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<OverallFilter>('all');

  // Đường dẫn hỏng thì đưa về hôm nay thay vì hiện trang trống khó hiểu.
  useEffect(() => {
    if (params.date && !isValidDateKey(params.date)) {
      navigate(`/day/${todayKey()}`, { replace: true });
    }
  }, [params.date, navigate]);

  const reload = useCallback(async () => {
    setLoadError(null);

    try {
      const [overview, unfinished] = await Promise.all([
        todosApi.getTodosForDay(date),
        todosApi.getUnfinishedTodos(date, 20)
      ]);

      setTodos(overview.todos ?? []);
      setSummary(overview.summary);
      setCarryOver(unfinished);
    } catch (caught) {
      setLoadError(toApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    setLoading(true);
    reload();
  }, [reload]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();

    return todos.filter(todo => {
      if (filter !== 'all' && todo.status?.overall !== filter) return false;
      if (needle && !(todo.title ?? '').toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [todos, search, filter]);

  const filtering = filter !== 'all' || search.trim() !== '';

  const commitOrder = useCallback(
    async (ordered: TodoBriefDto[]) => {
      const previous = todos;
      setTodos(ordered);

      try {
        await todosApi.reorderTodos(
          new ReorderTodosCommand({ todoDate: date, orderedIds: ordered.map(todo => todo.id!) })
        );
      } catch (caught) {
        setTodos(previous);
        showToast(toApiError(caught).message, { tone: 'danger' });
      }
    },
    [date, todos, showToast]
  );

  const drag = useDragReorder(visible, commitOrder);

  const remove = async (todo: TodoBriefDto) => {
    try {
      const snapshot = await todosApi.deleteTodo(todo.id!);

      setTodos(current => current.filter(item => item.id !== todo.id));

      showToast(t('todo.deleted', { title: todo.title ?? '' }), {
        durationMs: 5000,
        action: {
          label: t('todo.undo'),
          onClick: async () => {
            try {
              await todosApi.restoreTodo(snapshot);
              await reload();
              showToast(t('todo.restored'), { tone: 'success' });
            } catch (caught) {
              showToast(toApiError(caught).message, { tone: 'danger' });
            }
          }
        }
      });

      // Bảng tổng kết của ngày do máy chủ tính, nên phải hỏi lại thay vì tự trừ đi.
      reload();
    } catch (caught) {
      showToast(toApiError(caught).message, { tone: 'danger' });
    }
  };

  const moveHere = async (todo: TodoBriefDto) => {
    try {
      await todosApi.moveTodoToDay(todo.id!, new MoveTodoRequest({ todoDate: date }));
      await reload();
      showToast(t('day.moved', { title: todo.title ?? '' }), { tone: 'success' });
    } catch (caught) {
      showToast(toApiError(caught).message, { tone: 'danger' });
    }
  };

  return (
    <>
      <div className="day-row">
        <DateCard date={date} onPick={next => navigate(`/day/${next}`)} />

        <ProgressCard summary={summary} adding={adding} onToggleAdd={() => setAdding(open => !open)} />
      </div>

      {adding && (
        <CreateTodoForm
          date={date}
          onCreated={async () => {
            setAdding(false);
            await reload();
          }}
          onCancel={() => setAdding(false)}
        />
      )}

      {loading && <p className="text-body-secondary mt-4">{t('common.loading')}</p>}

      {loadError && (
        <div className="alert alert-danger d-flex justify-content-between align-items-center mt-4">
          <span>{loadError}</span>
          <button type="button" className="btn btn-sm btn-outline-danger" onClick={reload}>
            {t('common.retry')}
          </button>
        </div>
      )}

      {!loading && !loadError && todos.length === 0 && (
        <div className="empty-state">
          <h2 className="h5">{t('day.empty.title')}</h2>
          <p className="text-body-secondary mb-0">{t('day.empty.body')}</p>
        </div>
      )}

      {todos.length > 0 && (
        <div className="day-toolbar">
          <input
            className="form-control form-control-sm"
            placeholder={t('day.searchPlaceholder')}
            value={search}
            onChange={event => setSearch(event.target.value)}
            data-testid="todo-search"
          />
          <select
            className="form-select form-select-sm w-auto"
            value={filter}
            onChange={event => setFilter(event.target.value as OverallFilter)}
            aria-label={t('day.filterLabel')}
          >
            <option value="all">{t('day.filter.all')}</option>
            <option value={TodoProgressKind.NotCompleted}>{t('day.filter.notCompleted')}</option>
            <option value={TodoProgressKind.Partial}>{t('day.filter.partial')}</option>
            <option value={TodoProgressKind.Completed}>{t('day.filter.completed')}</option>
          </select>
        </div>
      )}

      {todos.length > 0 && visible.length === 0 && (
        <p className="text-body-secondary">{t('day.noMatch')}</p>
      )}

      {visible.length > 0 && (
        <>
          {filtering && <p className="text-body-secondary small mb-2">{t('day.dragLocked')}</p>}

          <ul className="todo-list list-unstyled" data-testid="todo-list">
            {drag.items.map((todo, index) => (
              <TodoCard
                key={todo.id}
                todo={todo}
                dragging={drag.draggingIndex === index}
                dragProps={filtering ? {} : drag.itemProps(index)}
                showHandle={!filtering}
                onDelete={() => remove(todo)}
              />
            ))}
          </ul>
        </>
      )}

      <CarryOverSection date={date} items={carryOver} onMove={moveHere} />
    </>
  );
}

function DateCard({ date, onPick }: { date: string; onPick: (next: string) => void }) {
  const { t, lang } = useI18n();

  return (
    <section className="panel date-card">
      <button
        type="button"
        className="date-arrow"
        onClick={() => onPick(addDays(date, -1))}
        title={t('day.prev')}
        aria-label={t('day.prev')}
      >
        <ChevronLeftIcon />
      </button>

      <div className="date-card-main">
        {/* Ô chọn ngày thật nằm trong suốt chồng lên icon: giữ nguyên bộ chọn ngày của trình duyệt
            mà bên ngoài chỉ thấy một cái icon lịch. */}
        <span className="date-picker-wrap">
          <span className="date-picker-button" aria-hidden="true">
            <CalendarIcon />
          </span>
          <input
            type="date"
            value={date}
            onChange={event => event.target.value && onPick(event.target.value)}
            aria-label={t('day.pick')}
            title={t('day.pick')}
          />
        </span>

        <span className="date-text" data-testid="day-title">
          {formatDayLong(date, lang)}
        </span>
      </div>

      <button
        type="button"
        className="date-arrow"
        onClick={() => onPick(addDays(date, 1))}
        title={t('day.next')}
        aria-label={t('day.next')}
      >
        <ChevronRightIcon />
      </button>
    </section>
  );
}

function ProgressCard({
  summary,
  adding,
  onToggleAdd
}: {
  summary: DaySummaryDto | undefined;
  adding: boolean;
  onToggleAdd: () => void;
}) {
  const { t } = useI18n();
  const [tipOpen, setTipOpen] = useState(false);

  const percent = Math.round(summary?.completionPercent ?? 0);
  const partial = summary?.taskPartial ?? 0;

  return (
    <section className="panel progress-card">
      <div className="progress-main">
        <div className="progress-caption">
          <strong>{t('day.summary.todos', { count: summary?.todoCount ?? 0 })}</strong>
          {' · '}
          {t('day.summary.done', { done: summary?.taskCompleted ?? 0, total: summary?.taskTotal ?? 0 })}
          {partial > 0 && ' · ' + t('day.summary.partial', { count: partial })}
        </div>

        <div
          className="progress-track"
          role="progressbar"
          aria-valuenow={percent}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className="progress-fill" style={{ width: `${percent}%` }} />
        </div>
      </div>

      <span className="progress-percent">{percent}%</span>

      <div
        className="add-fab-wrap"
        onMouseEnter={() => setTipOpen(true)}
        onMouseLeave={() => setTipOpen(false)}
      >
        <button
          type="button"
          className="add-fab"
          onClick={onToggleAdd}
          onFocus={() => setTipOpen(true)}
          onBlur={() => setTipOpen(false)}
          aria-label={t('day.addTooltip')}
          aria-expanded={adding}
          data-testid="add-todo"
        >
          {adding ? '×' : '+'}
        </button>

        {tipOpen && (
          <span className="tooltip-bubble" role="tooltip">
            {t('day.addTooltip')}
          </span>
        )}
      </div>
    </section>
  );
}

function TodoCard({
  todo,
  dragging,
  dragProps,
  showHandle,
  onDelete
}: {
  todo: TodoBriefDto;
  dragging: boolean;
  dragProps: DragItemProps | Record<string, never>;
  showHandle: boolean;
  onDelete: () => void;
}) {
  const { t, lang } = useI18n();

  const overall = todo.status?.overall ?? TodoProgressKind.NotCompleted;
  const priority = todo.priority ?? PriorityLevel.None;
  const done = overall === TodoProgressKind.Completed;
  const overdue = isOverdue(todo.targetTime) && !done;

  // Todo mới tạo chưa có công việc nào: nói thẳng ra như vậy, chứ "0/0 xong" đọc như một con số hỏng.
  const total = todo.status?.total ?? 0;
  const counts: string[] = [];

  if (total === 0) {
    counts.push(t('todo.counts.empty'));
  } else {
    counts.push(t('todo.counts.done', { done: todo.status?.completed ?? 0, total }));

    if (todo.status?.partial) counts.push(t('todo.counts.partial', { count: todo.status.partial }));
    if (todo.status?.notCompleted) {
      counts.push(t('todo.counts.notCompleted', { count: todo.status.notCompleted }));
    }
  }

  // "Ưu tiên cao" viết thường ở tiếng Việt, "High priority" viết hoa ở tiếng Anh.
  const levelLabel = t(PRIORITY_LABEL_KEY[priority]);
  const level = lang === 'vi' ? levelLabel.toLowerCase() : levelLabel;

  return (
    <li
      className={`panel todo-card ${PROGRESS_ACCENT_CLASS[overall]} ${done ? 'is-done' : ''} ${
        dragging ? 'is-dragging' : ''
      }`}
      {...dragProps}
    >
      {showHandle && (
        <span className="drag-handle" title={t('todo.dragHandle')} aria-hidden="true">
          ⠿
        </span>
      )}

      <div className="todo-card-body">
        <div className="todo-card-top">
          <Link className="todo-title" to={`/todo/${todo.id}`}>
            {todo.title}
          </Link>

          <div className="todo-tags">
            <span className={PROGRESS_TAG_CLASS[overall]}>{t(PROGRESS_LABEL_KEY[overall])}</span>
            {priority !== PriorityLevel.None && (
              <span className={PRIORITY_TAG_CLASS[priority]}>{t('priority.badge', { level })}</span>
            )}
          </div>
        </div>

        <div className="todo-card-meta">
          <span>{counts.join(' · ')}</span>

          {todo.targetTime && (
            <>
              <span className="meta-dot" aria-hidden="true" />
              <span className={`target-chip ${overdue ? 'is-overdue' : ''}`}>
                <ClockIcon />
                {t('todo.target', { time: formatClock(todo.targetTime) })}
              </span>
            </>
          )}
        </div>
      </div>

      <button
        type="button"
        className="btn btn-sm btn-outline-danger todo-delete"
        onClick={onDelete}
        aria-label={t('todo.deleteAria', { title: todo.title ?? '' })}
      >
        {t('todo.delete')}
      </button>
    </li>
  );
}

function CreateTodoForm({
  date,
  onCreated,
  onCancel
}: {
  date: string;
  onCreated: () => void;
  onCancel: () => void;
}) {
  const { t } = useI18n();
  const { showToast } = useToast();
  const inputRef = useRef<HTMLInputElement>(null);

  const [title, setTitle] = useState('');
  const [priority, setPriority] = useState<PriorityLevel>(PriorityLevel.None);
  const [targetTime, setTargetTime] = useState<TargetTimeInput>(
    new TargetTimeInput({ mode: TargetTimeMode.None })
  );
  const [busy, setBusy] = useState(false);

  useEffect(() => inputRef.current?.focus(), []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!title.trim()) return;

    setBusy(true);

    try {
      await todosApi.createTodo(
        new CreateTodoCommand({
          title: title.trim(),
          todoDate: date,
          priority,
          targetTime: targetTime.mode === TargetTimeMode.None ? undefined : targetTime
        })
      );

      onCreated();
    } catch (caught) {
      showToast(toApiError(caught).message, { tone: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="panel create-todo" onSubmit={submit}>
      <div className="d-flex gap-2">
        <input
          ref={inputRef}
          className="form-control"
          placeholder={t('day.newPlaceholder')}
          value={title}
          maxLength={200}
          onChange={event => setTitle(event.target.value)}
          onKeyDown={event => event.key === 'Escape' && onCancel()}
          data-testid="new-todo-title"
        />
        <button type="submit" className="btn btn-primary" disabled={busy || !title.trim()}>
          {t('day.add')}
        </button>
        <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>
          {t('day.cancel')}
        </button>
      </div>

      <div className="row g-2 mt-2">
        <div className="col-sm-4">
          <label className="form-label small text-body-secondary">{t('priority.label')}</label>
          <select
            className="form-select"
            value={priority}
            onChange={event => setPriority(event.target.value as PriorityLevel)}
          >
            {PRIORITY_ORDER.map(level => (
              <option key={level} value={level}>
                {t(PRIORITY_LABEL_KEY[level])}
              </option>
            ))}
          </select>
        </div>
        <div className="col-sm-8">
          <TargetTimeFields value={targetTime} onChange={setTargetTime} />
        </div>
      </div>
    </form>
  );
}

function CarryOverSection({
  date,
  items,
  onMove
}: {
  date: string;
  items: TodoBriefDto[];
  onMove: (todo: TodoBriefDto) => void;
}) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  if (items.length === 0) {
    return null;
  }

  return (
    <section className="carry-over">
      <button type="button" className="carry-toggle" onClick={() => setOpen(value => !value)}>
        {open ? '▾' : '▸'} {t('day.carryOver', { count: items.length })}
      </button>

      {open && (
        <ul className="list-unstyled mt-2 mb-0">
          {items.map(todo => (
            <li key={todo.id} className="carry-row">
              <span className="carry-date">{formatDayShort(todo.todoDate!)}</span>
              <Link className="flex-grow-1 todo-title" to={`/todo/${todo.id}`}>
                {todo.title}
              </Link>
              <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => onMove(todo)}>
                {t('day.moveHere', { date: formatDayShort(date) })}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
